import React from 'react';

import * as BioUtil from 'biojs/Util';
import * as sequtil from 'molbio/SeqUtil';
import {Enzyme} from 'profile/ProfileConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import makeDeprecatedJadeStyleTemplate from 'util/makeDeprecatedJadeStyleTemplate';

interface EmptySimpleSlotProps {
  empty: true;
  end: undefined;
  enzymes: undefined;
  index: number;
  length: undefined;
  name: undefined;
  reverse: undefined;
  seqLength: undefined;
  slotName: string;
  spacer: string;
  start: undefined;
  startIndex: undefined;
}

interface NonEmptySimpleSlotProps {
  empty: false;
  end: number;
  enzymes: Array<Enzyme>;
  index: number;
  length: number;
  name: string;
  reverse: boolean;
  seqLength: number;
  slotName: string;
  spacer: string;
  start: number;
  startIndex: number;
}

export type SimpleSlotProps = EmptySimpleSlotProps | NonEmptySimpleSlotProps;

function SimpleSlot({
  empty,
  end,
  enzymes,
  index,
  length,
  name,
  reverse,
  seqLength,
  slotName,
  spacer,
  start,
  startIndex,
}: SimpleSlotProps): JSX.Element {
  return (
    <div
      className="assembly-slot assembly-slot-single"
      {...getTestProps({component: 'SimpleSlot', element: 'root', index: `${index}`})}
      data-amp-safe={false}
    >
      {!empty ? (
        <div className="step-container slot-activator sequence-set">
          <div title={name} className="name">
            <span className="type all-caps">{index === 0 ? 'backbone' : 'insert'}</span>
            {name}
          </div>
          {enzymes == null ? (
            <div className="supplementary">
              {BioUtil.formatBaseCount(length)} &sdot; {sequtil.modelStartToDisplay(start, startIndex)}
              {' to '}
              {sequtil.modelEndToDisplay(end, seqLength, startIndex)}
              {reverse && ' (rev)'}
            </div>
          ) : reverse ? (
            <div className="supplementary">
              {BioUtil.formatBaseCount(length)} &sdot;{' '}
              <span style={{color: enzymes[1].color}}>{enzymes[1].name}</span>,{' '}
              <span style={{color: enzymes[0].color}}>{enzymes[0].name}</span>
            </div>
          ) : (
            <div className="supplementary">
              {BioUtil.formatBaseCount(length)} &sdot;{' '}
              <span style={{color: enzymes[0].color}}>{enzymes[0].name}</span>,{' '}
              <span style={{color: enzymes[1].color}}>{enzymes[1].name}</span>
            </div>
          )}
        </div>
      ) : spacer ? (
        <div className="step-container slot-activator has-spacer">
          <div className="name">{slotName}</div>
          <div className="supplementary">{spacer.length} bp</div>
        </div>
      ) : (
        <div className="step-container slot-activator">
          <div className="name">{slotName}</div>
        </div>
      )}
      <div className="assembly-slot-options">
        <div className="buttonGroup dropup">
          <button
            type="button"
            data-toggle="tooltip"
            data-placement="top"
            title="Move Left"
            className="button button--small button--tertiaryGray js-shift-left"
          >
            <span className="glyphicon glyphicon-chevron-left" />
          </button>
          <button
            type="button"
            data-toggle="tooltip"
            data-placement="top"
            title="Move Right"
            className="button button--small button--tertiaryGray js-shift-right"
          >
            <span className="glyphicon glyphicon-chevron-right" />
          </button>
          <div data-toggle="tooltip" title="Remove" className="buttonGroup">
            <button
              type="button"
              className="button button--small button--tertiaryGray js-delete u-danger-color"
            >
              <span className="glyphicon glyphicon-ban-circle" />
            </button>
          </div>
          <button
            type="button"
            data-toggle="tooltip"
            title="Add Spacer After"
            className="button button--small button--tertiaryGray js-insert-spacer"
          >
            <span className="glyphicon glyphicon-sound-stereo" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default makeDeprecatedJadeStyleTemplate<SimpleSlotProps>(SimpleSlot);
